<template>
  <section class='discovery-history' :aria-label='$t("discovery.history.title")'>
    <template v-if='summary && summary.cycles > 0'>
      <div class='discovery-history__header'>
        <div>
          <div class='discovery-history__title'>{{ $t('discovery.history.title') }}</div>
          <p class='discovery-history__stats'>{{ statsLine }}</p>
        </div>
        <button
          v-if='summary.cycles > 1'
          type='button'
          class='discovery-history__toggle'
          :aria-pressed='String(showList)'
          @click='showList = !showList'
        >
          {{ showList ? $t('discovery.history.view.chart') : $t('discovery.history.view.list') }}
        </button>
      </div>

      <p class='discovery-history__single' v-if='summary.cycles <= 1'>
        {{ $t('discovery.history.single', { source: sourceLabel(summary.sources[0]), date: formatDate(summary.firstSeen) }) }}
      </p>

      <template v-else>
        <div v-if='!showList' ref='card' class='discovery-history__card'>
          <svg
            class='discovery-history__chart'
            :width='width'
            :height='height'
            :viewBox='`0 0 ${width} ${height}`'
            role='img'
            :aria-label='statsLine'
          >
            <defs>
              <pattern :id='hatchId' width='6' height='6' patternUnits='userSpaceOnUse' patternTransform='rotate(45)'>
                <line x1='0' y1='0' x2='0' y2='6' class='discovery-history__hatch-line' />
              </pattern>
              <pattern :id='plannedHatchId' width='6' height='6' patternUnits='userSpaceOnUse' patternTransform='rotate(45)'>
                <line x1='0' y1='0' x2='0' y2='6' class='discovery-history__hatch-line discovery-history__hatch-line--planned' />
              </pattern>
            </defs>

            <!-- Episodes: one band per open period. -->
            <g v-for='(band, i) in episodeBands' :key='`band-${i}`'>
              <rect
                :x='band.x' :y='lanesTop' :width='band.width' :height='lanesHeight' rx='6'
                :class='band.fixed ? "discovery-history__band--fixed" : "discovery-history__band--open"'
              >
                <title>{{ band.title }}</title>
              </rect>
              <rect
                :x='band.x' :y='lanesTop' width='3' :height='lanesHeight' rx='1.5'
                :class='band.fixed ? "discovery-history__band-edge--fixed" : "discovery-history__band-edge--open"'
              />
            </g>

            <!-- Suppressed periods, and the planned rest of a temporary exception. -->
            <g v-for='(span, i) in suppressionSpans' :key='`supp-${i}`'>
              <rect
                :x='span.x' :y='lanesTop' :width='span.width' :height='lanesHeight' rx='4'
                :fill='`url(#${span.planned ? plannedHatchId : hatchId})`'
                :class='span.planned ? "discovery-history__supp--planned" : "discovery-history__supp"'
              >
                <title>{{ span.title }}</title>
              </rect>
            </g>

            <!-- Lanes: one per scan type. -->
            <g v-for='(lane, laneIndex) in lanes' :key='lane.source'>
              <text :x='0' :y='laneY(laneIndex) + 4' class='discovery-history__lane-label'>{{ sourceLabel(lane.source) }}</text>
              <line :x1='plotLeft' :x2='plotRight' :y1='laneY(laneIndex)' :y2='laneY(laneIndex)' class='discovery-history__lane-line' />
              <g v-for='(gap, gapIndex) in lane.gaps' :key='`gap-${gapIndex}`'>
                <line
                  :x1='gap.x1' :x2='gap.x2' :y1='laneY(laneIndex)' :y2='laneY(laneIndex)'
                  class='discovery-history__lane-line discovery-history__lane-line--quiet'
                >
                  <title>{{ gap.title }}</title>
                </line>
                <g v-if='gap.showLabel'>
                  <rect
                    :x='gap.mid - gap.labelWidth / 2' :y='laneY(laneIndex) - 8'
                    :width='gap.labelWidth' height='16' rx='8'
                    class='discovery-history__pill'
                  />
                  <text :x='gap.mid' :y='laneY(laneIndex) + 3.5' text-anchor='middle' class='discovery-history__pill-text'>
                    {{ gap.label }}
                    <title>{{ gap.title }}</title>
                  </text>
                </g>
              </g>
              <circle
                v-for='(tick, tickIndex) in lane.ticks'
                :key='tickIndex'
                :cx='tick.x' :cy='laneY(laneIndex)' :r='tick.r'
                :class='`discovery-history__dot discovery-history__dot--${laneIndex % 4}`'
              >
                <title>{{ tick.title }}</title>
              </circle>
            </g>

            <!-- Today. -->
            <line
              :x1='todayX' :x2='todayX' :y1='lanesTop - 4' :y2='lanesTop + lanesHeight + 4'
              class='discovery-history__today'
            />

            <!-- Fix / reopen markers above the lanes; events close together share one marker. -->
            <g v-for='(marker, i) in markers' :key='`marker-${i}`' class='discovery-history__marker'>
              <line
                :x1='marker.x' :x2='marker.x' :y1='markerY + 7' :y2='lanesTop + lanesHeight'
                class='discovery-history__marker-guide'
              />
              <rect
                :x='marker.x - marker.width / 2' :y='markerY - 8' :width='marker.width' height='16' rx='8'
                :class='`discovery-history__marker-dot--${marker.kind}`'
              >
                <title>{{ marker.title }}</title>
              </rect>
              <text :x='marker.x' :y='markerY + 3.5' text-anchor='middle' class='discovery-history__marker-symbol'>
                {{ marker.symbol }}
                <title>{{ marker.title }}</title>
              </text>
            </g>

            <!-- Axis labels, placed so they never overlap. -->
            <text
              v-for='(label, i) in axisLabels'
              :key='`axis-${i}`'
              :x='label.x' :y='axisY'
              :text-anchor='label.anchor'
              :class='["discovery-history__axis", { "discovery-history__axis--strong": label.strong }]'
            >{{ label.text }}</text>
          </svg>
        </div>

        <table v-else class='discovery-history__table'>
          <thead>
            <tr>
              <th scope='col'>{{ $t('discovery.history.col.found') }}</th>
              <th scope='col'>{{ $t('discovery.history.col.last.seen') }}</th>
              <th scope='col'>{{ $t('discovery.history.col.fixed') }}</th>
              <th scope='col'>{{ $t('discovery.history.col.cycles') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for='(episode, i) in history.episodes' :key='i'>
              <td>{{ formatDate(episode.start) }}<span v-if='episode.reopened' class='discovery-history__reopened'> · {{ $t('discovery.history.reopened') }}</span></td>
              <td>{{ formatDate(episode.lastSeen) }}</td>
              <td>{{ episode.fixed ? fixedText(episode) : $t('discovery.history.still.open') }}</td>
              <td>{{ episode.cycles }}</td>
            </tr>
          </tbody>
        </table>

        <ul class='discovery-history__legend' v-if='!showList'>
          <li><span class='discovery-history__swatch discovery-history__swatch--dot' />{{ $t('discovery.history.legend.detection') }}</li>
          <li v-if='hasOpenBand'><span class='discovery-history__swatch discovery-history__swatch--open' />{{ $t('discovery.history.legend.open') }}</li>
          <li v-if='hasFixedBand'><span class='discovery-history__swatch discovery-history__swatch--fixed' />{{ $t('discovery.history.legend.fixed') }}</li>
          <li v-if='hasPastSuppression'><span class='discovery-history__swatch discovery-history__swatch--suppressed' />{{ $t('discovery.history.legend.suppressed') }}</li>
          <li v-if='hasPlannedSuppression'><span class='discovery-history__swatch discovery-history__swatch--planned' />{{ $t('discovery.history.legend.planned') }}</li>
          <li v-if='hasFixMarker'><span class='discovery-history__glyph discovery-history__glyph--fix'>✓</span>{{ $t('discovery.history.legend.fix') }}</li>
          <li v-if='hasReopenMarker'><span class='discovery-history__glyph discovery-history__glyph--reopen'>↺</span>{{ $t('discovery.history.legend.reopen') }}</li>
        </ul>
      </template>
    </template>
  </section>
</template>

<script>
import moment from 'moment';

const DAY_MS = 24 * 60 * 60 * 1000;
/** Quiet periods longer than this get a dashed line and a duration label. */
const QUIET_GAP_DAYS = 60;
/** Above this many detections, dots are grouped per week. */
const WEEKLY_GROUPING_THRESHOLD = 200;
const LANE_HEIGHT = 32;
const MARKER_ROW = 22;
const AXIS_ROW = 22;
const RIGHT_PAD = 8;
/** Rough width of one character at the chart's 11px font, used to avoid overlapping labels. */
const CHAR_WIDTH = 6.4;
const MARKER_MERGE_PX = 20;
const MIN_SHAPE_WIDTH = 4;

const toMs = value => (value ? new Date(value).getTime() : null);
const textWidth = text => String(text).length * CHAR_WIDTH;

export default {
  name: 'DiscoveryHistory',
  props: {
    /** discoveryHistory from GET /issues/:id. */
    history: {
      type: Object,
      required: true,
    },
    /** suppressionHistory from GET /issues/:id, to shade suppressed periods. */
    suppressionHistory: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showList: false,
      width: 680,
      resizeObserver: null,
      now: Date.now(),
      markerY: MARKER_ROW / 2,
      lanesTop: MARKER_ROW + 4,
    };
  },
  computed: {
    hatchId() {
      // eslint-disable-next-line no-underscore-dangle
      return `discovery-hatch-${this._uid}`;
    },
    plannedHatchId() {
      // eslint-disable-next-line no-underscore-dangle
      return `discovery-hatch-planned-${this._uid}`;
    },
    summary() {
      return this.history && this.history.summary;
    },
    episodes() {
      return (this.history && this.history.episodes) || [];
    },
    lanesHeight() {
      return Math.max(this.lanes.length, 1) * LANE_HEIGHT;
    },
    axisY() {
      return this.lanesTop + this.lanesHeight + AXIS_ROW - 6;
    },
    height() {
      return this.lanesTop + this.lanesHeight + AXIS_ROW;
    },
    /** Lane labels get as much room as the longest one needs, at most 30% of the chart. */
    plotLeft() {
      const longest = Math.max(0, ...this.laneSources.map(s => textWidth(this.sourceLabel(s))));
      return Math.round(Math.min(Math.max(longest + 16, 64), this.width * 0.3));
    },
    plotRight() {
      return this.width - RIGHT_PAD;
    },
    statsLine() {
      const s = this.summary;
      const state = s.state === 'open'
        ? this.$t('discovery.history.state.open', { date: this.formatDate(s.openSince) })
        : this.$t('discovery.history.state.fixed', {
          date: `${s.fixedAtApproximate ? '~' : ''}${this.formatDate(s.fixedAt)}`,
        });
      return this.$t('discovery.history.stats', {
        cycles: this.$tc('discovery.history.cycles', s.cycles, { count: s.cycles }),
        sources: (s.sources || []).map(source => this.sourceLabel(source)).join(', '),
        first: this.formatDate(s.firstSeen),
        state,
      });
    },
    /** End of a temporary exception that is still running, so the chart can show what is planned. */
    plannedEnd() {
      const ends = (this.suppressionHistory || [])
        .filter(s => s.isEffective && !s.restoredAt && s.expiresAt)
        .map(s => toMs(s.expiresAt))
        .filter(t => t > this.now);
      return ends.length ? Math.max(...ends) : null;
    },
    domain() {
      const start = toMs(this.summary.firstSeen);
      const end = Math.max(this.now, toMs(this.summary.lastSeen) || 0, this.plannedEnd || 0);
      return { start, end };
    },
    /**
     * Time scale. Every interesting moment is a breakpoint; each interval between two
     * breakpoints gets width proportional to log(1 + days). Years of silence stay readable
     * next to a few days of activity, and the chart always fills the full width.
     */
    scale() {
      const { start, end } = this.domain;
      const clamp = t => Math.min(Math.max(t, start), end);
      const times = [start, end, this.now];
      (this.history.detections || []).forEach(d => times.push(toMs(d.day)));
      this.episodes.forEach((e, i) => {
        times.push(toMs(e.start));
        const to = this.episodeEnd(e, i);
        if (to) times.push(to);
      });
      (this.suppressionHistory || []).forEach(s => {
        times.push(toMs(s.suppressedAt));
        if (s.restoredAt) times.push(toMs(s.restoredAt));
        if (s.expiresAt) times.push(toMs(s.expiresAt));
      });
      const points = [...new Set(times.filter(t => t !== null && !Number.isNaN(t)).map(clamp))].sort((a, b) => a - b);
      const weights = points.slice(1).map((t, i) => Math.log1p((t - points[i]) / DAY_MS));
      const total = weights.reduce((sum, w) => sum + w, 0);
      const span = this.plotRight - this.plotLeft;
      const xs = [this.plotLeft];
      weights.forEach((w, i) => xs.push(xs[i] + (total > 0 ? (w / total) * span : 0)));
      return { points, xs };
    },
    todayX() {
      return this.xFor(this.now);
    },
    laneSources() {
      const sources = [];
      (this.history.detections || []).forEach(d => {
        if (!sources.includes(d.source)) sources.push(d.source);
      });
      return sources;
    },
    lanes() {
      const detections = this.history.detections || [];
      const weekly = detections.length > WEEKLY_GROUPING_THRESHOLD;
      return this.laneSources.map(source => {
        const buckets = new Map();
        detections.filter(d => d.source === source).forEach(d => {
          const bucket = weekly ? moment(d.day).startOf('isoWeek').valueOf() : toMs(d.day);
          buckets.set(bucket, (buckets.get(bucket) || 0) + d.count);
        });
        const times = [...buckets.keys()].sort((a, b) => a - b);
        const ticks = times.map(time => {
          const count = buckets.get(time);
          return {
            x: this.xFor(time),
            r: 4.5 + Math.min(2.5, Math.log2(count)),
            title: this.$tc(weekly ? 'discovery.history.tick.week' : 'discovery.history.tick.day', count, {
              count, source: this.sourceLabel(source), date: this.formatDate(time),
            }),
          };
        });
        return { source, ticks, gaps: this.quietGaps(times) };
      });
    },
    episodeBands() {
      return this.episodes.map((e, i) => {
        const from = toMs(e.start);
        const to = this.episodeEnd(e, i) || this.now;
        const x = this.xFor(from);
        return {
          x,
          width: Math.max(this.xFor(to) - x, MIN_SHAPE_WIDTH),
          fixed: e.fixed,
          title: e.fixed
            ? this.$t('discovery.history.band.fixed', { from: this.formatDate(e.start), to: this.fixedText(e), cycles: e.cycles })
            : this.$t('discovery.history.band.open', { from: this.formatDate(e.start), cycles: e.cycles }),
        };
      });
    },
    markers() {
      const events = [];
      this.episodes.forEach((e, i) => {
        if (e.reopened) {
          events.push({ x: this.xFor(toMs(e.start)), kind: 'reopen', title: this.$t('discovery.history.reopened.on', { date: this.formatDate(e.start) }) });
        }
        const fixedEnd = e.fixed ? this.episodeEnd(e, i) : null;
        if (fixedEnd) {
          events.push({ x: this.xFor(fixedEnd), kind: 'fix', title: this.$t('discovery.history.fixed.on', { date: this.fixedText(e) }) });
        }
      });
      events.sort((a, b) => a.x - b.x);
      // Events closer than one marker width share a pill showing both symbols.
      const merged = [];
      events.forEach(event => {
        const last = merged[merged.length - 1];
        if (last && event.x - last.x < MARKER_MERGE_PX) {
          last.title = `${last.title}\n${event.title}`;
          if (!last.kinds.includes(event.kind)) last.kinds.push(event.kind);
        } else {
          merged.push({ x: event.x, title: event.title, kinds: [event.kind] });
        }
      });
      return merged.map(m => {
        const kind = m.kinds.length > 1 ? 'both' : m.kinds[0];
        return {
          ...m,
          kind,
          symbol: m.kinds.map(k => (k === 'fix' ? '✓' : '↺')).join(' '),
          width: m.kinds.length > 1 ? 30 : 16,
        };
      });
    },
    suppressionSpans() {
      const spans = [];
      (this.suppressionHistory || []).forEach(s => {
        const from = toMs(s.suppressedAt);
        let to = this.now;
        if (s.restoredAt) {
          to = toMs(s.restoredAt);
        } else if (s.expiresAt && toMs(s.expiresAt) < this.now) {
          to = toMs(s.expiresAt);
        }
        let endLabel = this.$t('discovery.history.today');
        if (s.restoredAt) {
          endLabel = this.formatDate(s.restoredAt);
        } else if (s.expiresAt) {
          endLabel = this.formatDate(s.expiresAt);
        }
        const title = this.$t('discovery.history.suppressed.span', { from: this.formatDate(s.suppressedAt), to: endLabel });
        if (to >= from) {
          const x = this.xFor(from);
          spans.push({
            x, width: Math.max(this.xFor(to) - x, MIN_SHAPE_WIDTH), planned: false, title,
          });
        }
        const expires = toMs(s.expiresAt);
        if (s.isEffective && !s.restoredAt && expires && expires > this.now) {
          const x = this.xFor(this.now);
          spans.push({
            x, width: Math.max(this.xFor(expires) - x, MIN_SHAPE_WIDTH), planned: true, title,
          });
        }
      });
      return spans;
    },
    axisLabels() {
      const candidates = [
        {
          time: this.now, text: this.$t('discovery.history.today'), priority: 4, strong: true,
        },
        { time: this.domain.start, text: this.formatDate(this.domain.start), priority: 3 },
      ];
      if (this.plannedEnd) {
        candidates.push({ time: this.plannedEnd, text: this.formatDate(this.plannedEnd), priority: 2 });
      }
      this.episodes.filter(e => e.reopened).forEach(e => {
        candidates.push({ time: toMs(e.start), text: this.formatDate(e.start), priority: 1 });
      });
      return this.placeLabels(candidates);
    },
    hasOpenBand() {
      return this.episodeBands.some(b => !b.fixed);
    },
    hasFixedBand() {
      return this.episodeBands.some(b => b.fixed);
    },
    hasPastSuppression() {
      return this.suppressionSpans.some(s => !s.planned);
    },
    hasPlannedSuppression() {
      return this.suppressionSpans.some(s => s.planned);
    },
    hasFixMarker() {
      return this.markers.some(m => m.kinds.includes('fix'));
    },
    hasReopenMarker() {
      return this.markers.some(m => m.kinds.includes('reopen'));
    },
  },
  watch: {
    showList(list) {
      if (!list) this.$nextTick(this.measure);
    },
  },
  mounted() {
    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.$el);
    } else {
      window.addEventListener('resize', this.measure);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.measure);
    }
  },
  methods: {
    /** Draw at the real pixel width of the card so text and shapes are never stretched. */
    measure() {
      const { card } = this.$refs;
      const host = card || this.$el;
      if (!host || !host.clientWidth) {
        return;
      }
      const style = window.getComputedStyle(host);
      const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const width = Math.max(Math.floor(host.clientWidth - padding), 240);
      if (width !== this.width) {
        this.width = width;
      }
    },
    laneY(index) {
      return this.lanesTop + index * LANE_HEIGHT + LANE_HEIGHT / 2;
    },
    xFor(time) {
      const { points, xs } = this.scale;
      if (!points.length || time <= points[0]) {
        return xs[0] || this.plotLeft;
      }
      for (let i = 1; i < points.length; i += 1) {
        if (time <= points[i]) {
          const ratio = (time - points[i - 1]) / (points[i] - points[i - 1]);
          return xs[i - 1] + ratio * (xs[i] - xs[i - 1]);
        }
      }
      return xs[xs.length - 1];
    },
    /** When an episode stopped being open: its fix time, kept before the next episode began. */
    episodeEnd(episode, index) {
      if (!episode.fixed) {
        return null;
      }
      const fixedAt = toMs(episode.fixedAt) || toMs(episode.lastSeen);
      const next = this.episodes[index + 1];
      return next ? Math.min(fixedAt, toMs(next.start)) : fixedAt;
    },
    /** Long stretches without a detection on one lane (including up to today while still open). */
    quietGaps(times) {
      const ends = this.summary.state === 'open' ? [...times, this.now] : times;
      const gaps = [];
      for (let i = 1; i < ends.length; i += 1) {
        const ms = ends[i] - ends[i - 1];
        if (ms / DAY_MS > QUIET_GAP_DAYS) {
          const x1 = this.xFor(ends[i - 1]);
          const x2 = this.xFor(ends[i]);
          const label = this.durationLabel(ms);
          const labelWidth = textWidth(label) + 14;
          gaps.push({
            x1,
            x2,
            mid: (x1 + x2) / 2,
            label,
            labelWidth,
            // Only label gaps wide enough to hold the pill clear of the dots on either side.
            showLabel: x2 - x1 >= labelWidth + 24,
            title: this.$t('discovery.history.not.seen', { duration: label }),
          });
        }
      }
      return gaps;
    },
    /** Keep the most important labels; drop any that would overlap one already placed. */
    placeLabels(candidates) {
      const placed = [];
      [...candidates].sort((a, b) => b.priority - a.priority).forEach(c => {
        const w = textWidth(c.text);
        const x = this.xFor(c.time);
        let anchor = 'middle';
        let left = x - w / 2;
        if (left < 0) {
          anchor = 'start';
          left = Math.max(x, 0);
        } else if (x + w / 2 > this.width) {
          anchor = 'end';
          left = Math.min(x, this.width) - w;
        }
        const right = left + w;
        const overlaps = placed.some(p => left < p.right + 10 && right > p.left - 10);
        if (!overlaps) {
          const anchorX = { start: left, end: right, middle: x };
          placed.push({
            ...c, x: anchorX[anchor], anchor, left, right,
          });
        }
      });
      return placed;
    },
    durationLabel(ms) {
      const days = Math.round(ms / DAY_MS);
      if (days >= 365) {
        const years = Math.round(days / 365);
        return this.$tc('discovery.history.gap.years', years, { count: years });
      }
      if (days >= 45) {
        const months = Math.round(days / 30);
        return this.$tc('discovery.history.gap.months', months, { count: months });
      }
      return this.$tc('discovery.history.gap.days', days, { count: days });
    },
    formatDate(date) {
      return date ? moment(date).format('DD MMM YYYY') : '';
    },
    fixedText(episode) {
      if (!episode.fixedAt) {
        return this.$t('discovery.history.fixed');
      }
      return `${episode.fixedAtApproximate ? '~' : ''}${this.formatDate(episode.fixedAt)}`;
    },
    sourceLabel(source) {
      const key = `discovery.history.source.${source}`;
      return this.$te(key) ? this.$t(key) : (source || this.$t('discovery.history.source.unknown'));
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

$open: #2459d5;
$fixed: #2baf69;
$muted: #8a8f98;

.discovery-history {
  min-width: 0;
  margin: 0 $space-8 $space-6 0;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: $space-4;
  }

  &__title {
    color: var(--text-primary);
    font-family: $font-family-semibold;
    font-size: $font-size-base;
  }

  &__toggle {
    flex-shrink: 0;
    background: none;
    border: none;
    padding: 0;
    color: var(--brand-primary);
    font-size: $font-size-sm;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--brand-primary);
      outline-offset: 2px;
    }
  }

  &__stats,
  &__single {
    margin: $space-1 0 0;
    color: var(--text-secondary);
    font-size: $font-size-sm;
  }

  &__card {
    margin-top: $space-4;
    padding: $space-3 $space-4 $space-2;
    border-radius: $radius-sm;
    background-color: var(--bg-primary);
    box-shadow: 0 0 0 1px var(--border-primary);
  }

  &__chart {
    display: block;
    max-width: 100%;
    overflow: visible;
  }

  &__band--open { fill: rgba($open, 0.08); }
  &__band--fixed { fill: rgba($fixed, 0.1); }
  &__band-edge--open { fill: rgba($open, 0.55); }
  &__band-edge--fixed { fill: rgba($fixed, 0.6); }

  &__hatch-line {
    stroke: rgba($muted, 0.55);
    stroke-width: 2;

    &--planned {
      stroke: rgba($muted, 0.3);
    }
  }

  &__supp {
    stroke: rgba($muted, 0.5);
    stroke-width: 1;
  }

  &__supp--planned {
    stroke: rgba($muted, 0.45);
    stroke-width: 1;
    stroke-dasharray: 3 3;
  }

  &__lane-label {
    fill: var(--text-secondary);
    font-size: 12px;
  }

  &__lane-line {
    stroke: var(--border-primary);
    stroke-width: 1.5;

    &--quiet {
      stroke: var(--text-secondary);
      stroke-opacity: 0.45;
      stroke-dasharray: 2 4;
      stroke-linecap: round;
    }
  }

  &__pill {
    fill: var(--bg-primary);
    stroke: var(--border-primary);
    stroke-width: 1;
  }

  &__pill-text {
    fill: var(--text-secondary);
    font-size: 10.5px;
  }

  &__dot {
    stroke: var(--bg-primary);
    stroke-width: 2;
    transition: transform 150ms ease;
    transform-box: fill-box;
    transform-origin: center;

    &:hover {
      transform: scale(1.25);
    }

    &--0 { fill: $open; }
    &--1 { fill: #7a4fd1; }
    &--2 { fill: #1b8a8f; }
    &--3 { fill: #5a6b7d; }
  }

  &__today {
    stroke: var(--text-primary);
    stroke-opacity: 0.55;
    stroke-width: 1;
    stroke-dasharray: 3 3;
  }

  &__marker-guide {
    stroke: var(--text-secondary);
    stroke-opacity: 0.3;
    stroke-width: 1;
    stroke-dasharray: 2 3;
  }

  &__marker-dot--fix {
    fill: rgba($fixed, 0.15);
    stroke: $fixed;
    stroke-width: 1.25;
  }

  &__marker-dot--reopen {
    fill: rgba($open, 0.12);
    stroke: $open;
    stroke-width: 1.25;
  }

  &__marker-dot--both {
    fill: var(--bg-primary);
    stroke: var(--text-secondary);
    stroke-opacity: 0.6;
    stroke-width: 1.25;
  }

  &__marker-symbol {
    fill: var(--text-primary);
    font-size: 10px;
    pointer-events: none;
  }

  &__axis {
    fill: var(--text-secondary);
    font-size: 11px;

    &--strong {
      fill: var(--text-primary);
      font-family: $font-family-medium;
    }
  }

  &__table {
    width: 100%;
    margin-top: $space-4;
    border-collapse: collapse;
    font-size: $font-size-sm;

    th,
    td {
      text-align: left;
      padding: $space-2;
      border-bottom: $border-width-thin solid var(--border-primary);
    }

    th {
      color: var(--text-secondary);
      font-weight: normal;
      text-transform: uppercase;
      font-size: $font-size-xs;
      letter-spacing: 0.6px;
    }
  }

  &__reopened {
    color: var(--text-secondary);
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: $space-2 $space-4;
    margin: $space-3 0 0;
    padding: 0;
    list-style: none;
    color: var(--text-secondary);
    font-size: $font-size-xs;

    li {
      display: inline-flex;
      align-items: center;
      gap: rem(6);
    }
  }

  &__swatch {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;

    &--dot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: $open;
    }
    &--open { background: rgba($open, 0.14); box-shadow: inset 3px 0 0 rgba($open, 0.55); }
    &--fixed { background: rgba($fixed, 0.16); box-shadow: inset 3px 0 0 rgba($fixed, 0.6); }
    &--suppressed {
      background: repeating-linear-gradient(45deg, rgba($muted, 0.55) 0 2px, transparent 2px 5px);
      box-shadow: inset 0 0 0 1px rgba($muted, 0.5);
    }
    &--planned {
      background: repeating-linear-gradient(45deg, rgba($muted, 0.3) 0 2px, transparent 2px 5px);
      outline: 1px dashed rgba($muted, 0.6);
      outline-offset: -1px;
    }
  }

  &__glyph {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    font-size: 9px;
    color: var(--text-primary);

    &--fix { background: rgba($fixed, 0.15); box-shadow: inset 0 0 0 1px $fixed; }
    &--reopen { background: rgba($open, 0.12); box-shadow: inset 0 0 0 1px $open; }
  }

  @media (max-width: 600px) {
    margin-right: $space-4;

    &__card {
      padding: $space-2;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &__dot {
      transition: none;

      &:hover {
        transform: none;
      }
    }
  }
}
</style>
